import { ElementType, useState } from "react";

import {
  Box,
  Collapse,
  Drawer,
  ListItemIcon,
  ListItemText,
  List as MUIList,
  ListItemButton as MUIListItemButton,
  Typography as MUITypography,
  Popover,
  Stack,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import { Paths, SandboxUrlLoginRedirect, UseLoginLocal } from "configs";
import { shadows } from "configs/shadows";
import { useAppTheme } from "contexts";
import { useIsMobile, usePermission } from "hooks";
import { UserAvatar } from "pages/Components";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAppSelector } from "stores";
import { saveLocalStorage } from "utils";
import { getPrimaryColor } from "utils/theme";

import { useSidebar } from "./Sidebar.hooks";
import {
  Container,
  Divider,
  Flag,
  List,
  ListItemButton,
  Logo,
  LogoWrapper,
  LogoutButton,
  MenuBarPlaceholder,
  MenuBarTrack,
  MenuContainer,
  MenuScrollbar,
  MenuWrapper,
  SubList,
  UserInfo,
  UserWrapper,
} from "./Sidebar.styles";
import { SidebarMenuItem, SidebarProps } from "./Sidebar.types";
import {
  Building04Icon,
  CheckIcon,
  ChevronRightIcon,
  LogOut01Icon,
} from "../Icon";
import Typography from "../Typography";

export const COPYRIGHT_URL = "https://www.talentnet.vn/";

const checkSubmenuActive = (submenu: SidebarMenuItem[]): boolean => {
  let active = false;
  for (const s of submenu) {
    if (s.path && window.location.pathname.includes(s.path)) {
      active = true;
      break;
    }
    if (Array.isArray(s.children) && s.children.length > 0) {
      active = checkSubmenuActive(s.children);
      if (active) break;
    }
  }
  return active;
};

const MenuRendering = (
  data: SidebarProps["menu"],
  level: number,
  collapsed: boolean,
  noIcon = false
) => {
  const location = useLocation();
  const { expandSidebarHover } = useAppTheme();
  const { canAccess } = usePermission();

  const [menuId, setMenuId] = useState<string | null>(null);

  const onClickHandler = (id: string) => {
    setMenuId((prev) => (prev === id ? null : id));
    if (collapsed) {
      expandSidebarHover?.(true);
    }
  };

  return data
    .map(
      ({
        icon,
        id,
        path,
        title,
        disabled = false,
        children = [],
        permissions,
      }) => {
        const isChildrenEmpty = children.length === 0;
        const isOpen = menuId === id;
        const isHyperlink = !!path;
        let isActive = false;
        if (isHyperlink) {
          isActive =
            path === Paths.App
              ? location.pathname === Paths.App
              : location.pathname.includes(path);
        } else {
          isActive = checkSubmenuActive(children);
        }
        const isSubHasChild = level === 2 && children.length > 0;
        if (
          isChildrenEmpty &&
          permissions?.length &&
          !permissions?.every((permission) => canAccess(permission as never))
        ) {
          return null;
        }
        return (
          <Box key={id}>
            <ListItemButton
              to={isHyperlink ? path : undefined}
              component={isHyperlink ? RouterLink : undefined}
              onClick={() => (!isChildrenEmpty ? onClickHandler(id) : null)}
              level={level}
              open={isOpen}
              collapsed={collapsed}
              active={isActive}
              disabled={disabled}
              isSubHasChild={isSubHasChild}
            >
              <ListItemIcon>
                {!noIcon && (
                  <SvgIcon
                    fontSize="small"
                    component={icon as unknown as ElementType}
                  />
                )}
              </ListItemIcon>
              <ListItemText primary={title} />
              {!isChildrenEmpty ? <ChevronRightIcon /> : null}
            </ListItemButton>
            {!isChildrenEmpty ? (
              <Collapse in={!collapsed ? isOpen : false}>
                <SubList level={level} numberOfChildren={children?.length ?? 0}>
                  {MenuRendering(children, level + 1, collapsed, true)}
                </SubList>
              </Collapse>
            ) : null}
          </Box>
        );
      }
    )
    .filter(Boolean);
};

const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  menu,
  bottomMenu,
  userInfo,
  languages,
  defaultLanguage,
  onHover,
  onBlur,
  onLogout,
  onLanguageChange,
}) => {
  const { language, organization, goToProfile } = useSidebar({
    defaultLanguage,
    languages,
    onBlur,
    onLanguageChange,
  });

  const currentOrganization = useAppSelector(
    (state) => state?.auth?.currentOrganization
  );
  const currentLanguage = languages?.find((i) => i.id === language.value);
  const isMobile = useIsMobile();
  const { expandSidebarHover } = useAppTheme();

  return (
    <Container
      collapsed={collapsed}
      onMouseEnter={onHover}
      onMouseLeave={onBlur}
      isMobile={isMobile}
    >
      <Drawer
        open={isMobile ? !collapsed : true}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        PaperProps={{
          sx: {
            overflowX: "hidden",
            backgroundColor: getPrimaryColor()[700],
            width: isMobile ? "280px" : "unset",
          },
        }}
        onClose={() => {
          expandSidebarHover?.(false);
        }}
      >
        <Stack
          justifyContent="space-between"
          height="100%"
          gap={4}
          paddingY={4}
        >
          <LogoWrapper href={UseLoginLocal ? "/" : SandboxUrlLoginRedirect}>
            <Logo
              collapsed={collapsed}
              logo={currentOrganization?.settings?.logo || ""}
              shortLogo={currentOrganization?.settings?.shortLogo || ""}
            />
          </LogoWrapper>
          <Divider />
          <MenuContainer className="menu-container">
            <MenuWrapper className="menu-wrapper">
              <List className="menu-list" component="nav">
                <ListItemButton
                  level={1}
                  collapsed={collapsed}
                  onClick={organization.open}
                >
                  <Building04Icon fontSize="small" />
                  <ListItemText primary={organization.value?.name} />
                </ListItemButton>
                <Divider sx={{ marginTop: "12px" }} />
                {MenuRendering(menu, 1, collapsed)}
              </List>
              <List component="nav">
                {MenuRendering(bottomMenu, 1, collapsed)}
                {currentLanguage ? (
                  <ListItemButton
                    level={1}
                    collapsed={collapsed}
                    onClick={language.open}
                  >
                    <Flag
                      src={currentLanguage.flagIcon}
                      alt={currentLanguage.name}
                    />
                    <ListItemText primary={currentLanguage.name} />
                  </ListItemButton>
                ) : null}
              </List>
            </MenuWrapper>
            <MenuBarTrack className="menu-bar-track">
              <MenuBarPlaceholder className="menu-bar-placeholder" />
              <MenuScrollbar className="menu-scrollbar" />
            </MenuBarTrack>
          </MenuContainer>
          {userInfo ? (
            <>
              {/* {!collapsed && (
                  <>
                    <Divider />
                    <MUITypography
                      fontWeight={500}
                      onClick={() => {
                        window.open(COPYRIGHT_URL, "_blank");
                      }}
                      sx={{
                        cursor: "pointer",
                        textAlign: "center",
                        color: "white",
                      }}
                    >
                      {currentOrganization?.settings?.copyrightText ||
                        "POWERED BY TALENTNET"}
                    </MUITypography>
                  </>
                )} */}
              <Divider />
              <UserWrapper>
                <UserAvatar
                  name={userInfo.name}
                  onClick={() => {
                    goToProfile();
                  }}
                />
                <UserInfo collapsed={collapsed}>
                  <Tooltip arrow title={userInfo.name}>
                    <MUITypography
                      fontWeight={600}
                      onClick={goToProfile}
                      sx={{ cursor: "pointer" }}
                    >
                      {userInfo.name}
                    </MUITypography>
                  </Tooltip>
                  <Tooltip arrow title={userInfo.email}>
                    <MUITypography>{userInfo.email}</MUITypography>
                  </Tooltip>
                </UserInfo>
                <LogoutButton collapsed={collapsed} onClick={onLogout}>
                  <LogOut01Icon fontSize="small" color="inherit" />
                </LogoutButton>
              </UserWrapper>
            </>
          ) : null}
        </Stack>
      </Drawer>
      {languages && languages?.length > 0 ? (
        <Popover
          open={!!language.ref}
          anchorEl={language.ref}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { boxShadow: shadows.xs } } }}
          onClose={language.close}
        >
          <MUIList>
            {languages.map((i) => (
              <MUIListItemButton
                key={i.id}
                onClick={() => {
                  language.change(i.id);
                  saveLocalStorage("lan", i.id);
                  window.location.reload();
                }}
              >
                <Stack width="100%" direction="row" alignItems="center" gap={2}>
                  <Flag src={i.flagIcon} alt={i.name} />
                  <Typography type="TextSM" fontWeight={500} flex={1}>
                    {i.name}
                  </Typography>
                </Stack>
              </MUIListItemButton>
            ))}
          </MUIList>
        </Popover>
      ) : null}
      {organization?.options?.length && (
        <Popover
          open={!!organization.ref}
          anchorEl={organization.ref}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { boxShadow: shadows.xs } } }}
          onClose={organization.close}
        >
          <MUIList>
            {organization?.options.map((i) => (
              <MUIListItemButton
                key={i.id}
                onClick={() => {
                  organization.change(i.id);
                }}
              >
                <Stack
                  sx={{ "& svg": { color: getPrimaryColor()[500] } }}
                  width="100%"
                  direction="row"
                  alignItems="center"
                  gap={2}
                >
                  <Typography type="TextSM" fontWeight={500} flex={1}>
                    {i.name}
                  </Typography>
                  {i.id === organization.value?.id ? (
                    <CheckIcon fontSize="small" />
                  ) : null}
                </Stack>
              </MUIListItemButton>
            ))}
          </MUIList>
        </Popover>
      )}
    </Container>
  );
};

export default Sidebar;
